import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Marker, Tooltip } from 'react-leaflet'
import L from 'leaflet'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faLink } from '@fortawesome/free-solid-svg-icons'

import type { OperationInputs, OperationMapPoint, OperatorDef, OperatorId } from '../operations'
import { googleMapsLocationUrl } from '../operations'

import { MapEntityPopupShell, operatorDocksPopupToCorner } from './MapEntityPopups'
import { createInvisiblePopupAnchorIcon, createResultIcon } from './mapMarkerIcons'
import { copyTextToClipboard } from './mapEntityPresentation'
import { dragPayloadForResultPoint, useMapEntityDrag } from './useEntityDrag'
import { OperatorPopupControls, useOperatorPopupState } from './OperatorPopupControls'

export function OperationResultMarker({
  point,
  fromStepId,
  iconDef,
  focused,
  onFocus,
  operators = [],
  onRunOperator,
  operationRunning = false,
  destinationOptions = [],
  preferredDestinationRef = '',
  operationResultOperatorId = null,
  suppressPopup = false,
  hidePin = false,
}: {
  point: OperationMapPoint
  /** Step that owns this map result — used for drag → stepPointRef drops. */
  fromStepId: string
  iconDef: IconDefinition
  focused: boolean
  onFocus: (pointId: string) => void
  operators?: OperatorDef[]
  onRunOperator?: (point: OperationMapPoint, operatorId: OperatorId, inputs: OperationInputs) => void
  onInputsEdit?: (inputs: OperationInputs) => void
  operationRunning?: boolean
  destinationOptions?: Array<{ id: string; label: string; group?: string; keywords?: string }>
  preferredDestinationRef?: string
  operationResultOperatorId?: OperatorId | null
  /** Mobile: skip the Leaflet/docked info popup (ops live in the output panel). */
  suppressPopup?: boolean
  /** Hide the visible pin while keeping an invisible popup anchor. */
  hidePin?: boolean
}) {
  const markerRef = useRef<L.Marker | null>(null)
  const [mapsLinkCopied, setMapsLinkCopied] = useState(false)
  const mapsLinkCopiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isExternalResult = !point.sourceEntity
  const mapsLocationUrl = isExternalResult
    ? googleMapsLocationUrl(point.lat, point.lng)
    : null
  const dockToCorner = operatorDocksPopupToCorner(operationResultOperatorId)
  const dragPayload = useMemo(
    () => (hidePin ? null : dragPayloadForResultPoint(point, fromStepId)),
    [point, fromStepId, hidePin],
  )
  const suppressClickRef = useMapEntityDrag({
    markerRef,
    payload: dragPayload,
    remountKey: `${focused ? '1' : '0'}:${hidePin ? '1' : '0'}`,
  })
  const icon = useMemo(
    () =>
      hidePin
        ? createInvisiblePopupAnchorIcon()
        : createResultIcon(point.color, iconDef, focused),
    [point.color, iconDef, focused, hidePin],
  )

  const onRun = useCallback(
    (op: OperatorDef, inputs: OperationInputs) => {
      onRunOperator?.(point, op.id, inputs)
    },
    [point, onRunOperator],
  )

  const {
    activeOperatorId,
    activeOperator,
    draftInputs,
    popupDismissed,
    lockPopupForEntityDrop,
    dismissPopup,
    revealPopup,
    selectOperator,
    patchInputs,
    selectDestinationEntity,
    runActiveOperator,
    destinationOptional,
    needsDestination,
    canManualRun,
    showRunBesideRadius,
    showRunBesideRange,
    showRunAtEnd,
    isRerun,
    runLabel,
  } = useOperatorPopupState({
    operators: suppressPopup ? [] : operators,
    preferredDestinationRef,
    operationResultOperatorId,
    operationRunning,
    isActive: focused && !suppressPopup,
    onRun: !suppressPopup && onRunOperator ? onRun : undefined,
  })

  useEffect(() => {
    return () => {
      if (mapsLinkCopiedTimeoutRef.current) clearTimeout(mapsLinkCopiedTimeoutRef.current)
    }
  }, [])

  const copyMapsLocationLink = async () => {
    if (!mapsLocationUrl) return
    const ok = await copyTextToClipboard(mapsLocationUrl)
    if (!ok) return
    setMapsLinkCopied(true)
    if (mapsLinkCopiedTimeoutRef.current) clearTimeout(mapsLinkCopiedTimeoutRef.current)
    mapsLinkCopiedTimeoutRef.current = setTimeout(() => setMapsLinkCopied(false), 2000)
  }

  useEffect(() => {
    const marker = markerRef.current
    if (!marker) return
    if (suppressPopup || !focused || popupDismissed || dockToCorner) marker.closePopup()
    else marker.openPopup()
  }, [focused, dockToCorner, popupDismissed, suppressPopup])

  return (
    <Marker
      ref={markerRef}
      position={{ lat: point.lat, lng: point.lng }}
      icon={icon}
      interactive={!hidePin}
      zIndexOffset={hidePin ? 100 : focused ? 1100 : 900}
      eventHandlers={
        hidePin
          ? undefined
          : {
              click: () => {
                if (suppressClickRef.current) return
                if (!suppressPopup) revealPopup()
                onFocus(point.id)
              },
            }
      }
    >
      {hidePin ? null : (
        <Tooltip direction="top" offset={[0, -14]} opacity={0.96}>
          <div className="max-w-[14rem]">
            <div className="text-xs font-semibold leading-tight">{point.name}</div>
            {point.subtitle ? (
              <div className="mt-0.5 text-[10px] leading-snug opacity-80">{point.subtitle}</div>
            ) : null}
          </div>
        </Tooltip>
      )}
      {suppressPopup ? null : (
        <MapEntityPopupShell
          docked={dockToCorner}
          open={focused && !popupDismissed}
          lockOpen={lockPopupForEntityDrop}
          minWidth={operators.length > 0 ? 280 : undefined}
          onDismiss={dismissPopup}
        >
          <div
            className={
              operators.length > 0
                ? 'min-w-[16rem] max-w-[20rem]'
                : 'min-w-[10rem] max-w-[16rem]'
            }
          >
            <div className="flex items-start gap-2">
              <span
                className="mt-1 h-2.5 w-2.5 shrink-0 rounded-sm"
                style={{ backgroundColor: point.color }}
                aria-hidden
              />
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-base-content/45">
                  Map result
                </div>
                <div className="text-sm font-semibold leading-tight text-base-content">
                  {point.name}
                </div>
                {point.subtitle ? (
                  <div className="mt-1 text-xs leading-snug text-base-content/70">{point.subtitle}</div>
                ) : null}
                <div className="mt-1.5 text-[10px] text-base-content/45">
                  {point.lat.toFixed(5)}, {point.lng.toFixed(5)}
                </div>
                {mapsLocationUrl ? (
                  <button
                    type="button"
                    className={`btn btn-xs mt-2 gap-1.5 ${
                      mapsLinkCopied ? 'btn-success' : 'btn-ghost border border-base-300'
                    }`}
                    aria-label={
                      mapsLinkCopied ? 'Google Maps link copied' : 'Copy Google Maps link'
                    }
                    title={
                      mapsLinkCopied
                        ? 'Copied!'
                        : 'Copy Google Maps link for this location'
                    }
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      void copyMapsLocationLink()
                    }}
                  >
                    <FontAwesomeIcon
                      icon={mapsLinkCopied ? faCheck : faLink}
                      className="text-[10px]"
                    />
                    {mapsLinkCopied ? 'Copied' : 'Copy Maps link'}
                  </button>
                ) : null}
              </div>
            </div>
            {operators.length > 0 && onRunOperator ? (
              <OperatorPopupControls
                variant="result"
                operators={operators}
                operationRunning={operationRunning}
                destinationOptions={destinationOptions}
                activeOperatorId={activeOperatorId}
                activeOperator={activeOperator}
                draftInputs={draftInputs}
                selectOperator={selectOperator}
                patchInputs={patchInputs}
                selectDestinationEntity={selectDestinationEntity}
                runActiveOperator={runActiveOperator}
                destinationOptional={destinationOptional}
                needsDestination={needsDestination}
                canManualRun={canManualRun}
                showRunBesideRadius={showRunBesideRadius}
                showRunBesideRange={showRunBesideRange}
                showRunAtEnd={showRunAtEnd}
                isRerun={isRerun}
                runLabel={runLabel}
              />
            ) : null}
          </div>
        </MapEntityPopupShell>
      )}
    </Marker>
  )
}
